import React, { useEffect, useState, useRef } from 'react';
import { Carousel, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import { IconChevronLeft, IconChevronRight, IconLayoutGrid } from '@tabler/icons-react';
import SafeImage from './SafeImage';
import { useCategory } from '../context/CategoryContext';
import { usePreference } from '../context/PreferenceContext';
import { capitalizeFirst, renderSafeText } from '../util/unitFormatter';
import '../css/categorySwiper.css';

const DEFAULT_CATEGORY_ICONS = [
  "https://images.unsplash.com/photo-1518770660439-4636190af475?w=200&h=200&q=80&fit=crop", // Tech
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=200&h=200&q=80&fit=crop", // Furniture
  "https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=200&h=200&q=80&fit=crop", // Auto
  "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200&h=200&q=80&fit=crop", // Hardware
  "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=200&h=200&q=80&fit=crop", // Appliances
  "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=200&h=200&q=80&fit=crop", // Products
];

const CategorySwiper = ({ className = '', style = {}, categoryId = null, activeName = null, onSelect = null }) => {
  const { t } = usePreference();
  const { categories, subCategories, loading } = useCategory();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const carouselRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter and map items
  const filteredSubCategories = categoryId
    ? subCategories.filter(cat => {
        const catParentId = cat.parentId?._id || cat.parentId;
        return catParentId === categoryId;
      })
    : subCategories; // On Home page, show all subcategories

  const mappedItems = filteredSubCategories.map(cat => ({
    ...cat,
    isActive: renderSafeText(cat.name).toLowerCase() === activeName?.toLowerCase(),
    onClick: () => {
      const name = renderSafeText(cat.name);
      if (onSelect) {
        onSelect(name);
      } else {
        const parentId = cat.parentId?._id || cat.parentId;
        const parent = categories.find(c => (c._id || c.id) === parentId);
        if (parent) {
          navigate(`/category/${encodeURIComponent(renderSafeText(parent.name))}?sub=${encodeURIComponent(name)}`);
        } else {
          navigate(`/category/${encodeURIComponent(name)}`);
        }
      }
    }
  }));

  const items = [];
  if (categoryId) {
    // Add "All" option if we are within a parent category
    items.push({
      _id: 'all-sub',
      name: 'All',
      icon: <IconLayoutGrid size={18} />,
      isActive: activeName?.toLowerCase() === 'all' || !activeName,
      onClick: () => {
        if (onSelect) {
          onSelect('All');
        } else {
          const cat = categories.find(c => (c._id || c.id) === categoryId);
          if (cat) {
            navigate(`/category/${encodeURIComponent(renderSafeText(cat.name))}`);
          }
        }
      }
    });
  }
  items.push(...mappedItems);

  const getSlidesToShow = (width) => {
    if (width > 1024) return 12;
    if (width > 768) return 6;
    if (width > 480) return 4;
    return 3;
  };

  const slidesToShow = getSlidesToShow(windowWidth);
  const isSlider = items.length > slidesToShow;

  const carouselSettings = {
    infinite: items.length > 8,
    autoplay: true,
    autoplaySpeed: 5500,
    dots: false,
    speed: 50,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    initialSlide: 0,
    pauseOnHover: true,
    responsive: [
      { breakpoint: 1440, settings: { slidesToShow: 10, slidesToScroll: 1 } },
      { breakpoint: 1024, settings: { slidesToShow: 8, slidesToScroll: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 6, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 4, slidesToScroll: 1 } }
    ]
  };

  if (loading) {
    return (
      <div className={`category-swiper-container ${className}`} style={{ ...style, padding: '20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', overflow: 'hidden', flexWrap: 'nowrap' }}>
          {[...Array(Math.max(8, slidesToShow))].map((_, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', minWidth: '80px', flexShrink: 0 }}>
              <div className="category-img-wrapper" style={{ background: '#fdfdfd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Skeleton.Avatar active shape="square" style={{ width: '100%', height: '100%', borderRadius: '10px' }} />
              </div>
              <Skeleton.Input active size="small" style={{ width: 60, height: 14, minWidth: '60px', borderRadius: '4px' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`category-swiper-container ${className} ${!isSlider ? 'flex-mode' : ''}`} style={{ ...style }}>
      {isSlider && (
        <>
          <button
            className="custom-nav custom-prev"
            onClick={() => carouselRef.current.prev()}
          >
            <IconChevronLeft size={20} />
          </button>
          <button
            className="custom-nav custom-next"
            onClick={() => carouselRef.current.next()}
          >
            <IconChevronRight size={20} />
          </button>
        </>
      )}

      {isSlider ? (
        <Carousel ref={carouselRef} {...carouselSettings} className="category-swiper">
          {items.map((item, index) => (
            <div key={item._id} className="category-slide-item">
              <div className={`category-item ${item.isActive ? 'active' : ''} ${index === 0 ? 'is-first' : ''}`} onClick={item.onClick} title={typeof item.name === 'object' ? item.name.name : item.name}>
                <div className="category-img-wrapper">
                  {item.icon ? (
                    <div className="category-icon-inner">{item.icon}</div>
                  ) : (
                    <SafeImage
                      src={item.imageUrl || DEFAULT_CATEGORY_ICONS[index % DEFAULT_CATEGORY_ICONS.length]}
                      alt={renderSafeText(item.name)}
                      placeholderText={renderSafeText(item.name)}
                      variant="square"
                    />
                  )}
                </div>
                <span className="category-name" title={typeof item.name === 'object' ? item.name.name : item.name}>
                  {capitalizeFirst(typeof item.name === 'object' ? (item.name.name || JSON.stringify(item.name)) : item.name)}
                </span>
              </div>
            </div>
          ))}
        </Carousel>
      ) : (
        <div className="category-swiper-flex">
          {items.map((item, index) => (
            <div key={item._id} className="category-flex-item">
              <div className={`category-item ${item.isActive ? 'active' : ''} ${index === 0 ? 'is-first' : ''}`} onClick={item.onClick} title={typeof item.name === 'object' ? item.name.name : item.name}>
                <div className="category-img-wrapper">
                  {item.icon ? (
                    <div className="category-icon-inner">{item.icon}</div>
                  ) : (
                    <SafeImage
                      src={item.imageUrl || DEFAULT_CATEGORY_ICONS[index % DEFAULT_CATEGORY_ICONS.length]}
                      alt={renderSafeText(item.name)}
                      placeholderText={renderSafeText(item.name)}
                      variant="square"
                    />
                  )}
                </div>
                <span className="category-name" title={typeof item.name === 'object' ? item.name.name : item.name}>
                  {capitalizeFirst(typeof item.name === 'object' ? (item.name.name || JSON.stringify(item.name)) : item.name)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryItem = ({ name, img }) => (
  <div className="category-item">
    <div className="category-img-wrapper">
      <img
        src={img || "https://placehold.co/100x100"}
        alt={name}
        onError={(e) =>
          (e.target.src = "https://placehold.co/100x100")
        }
      />
    </div>
    <span className="category-name" title={name}>
      {capitalizeFirst(name)}
    </span>
  </div>
);

export default CategorySwiper;
